import { Injectable } from '@angular/core';
import { ApiRequestService } from '@app/core/http/api-request.service';
import { ApiResponse, FilterObject, ItemProps, MenuList, SystemPermission, SystemPermissions } from '@app/interfaces';
import { map } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { AppHelper } from '@app/core/classes/app-helper';
import { DynamicDialogComponent } from '@app/shared/components/dynamic-dialog/dynamic-dialog.component';
import { SharedService } from '@app/shared/services/shared.service';
import { ImageItem } from '@ngx-gallery/core';
import { DummyData } from '@app/shared/services/dummy-data';
import * as moment from 'moment';
import customEnvironment from '@env/environment.custom.json';
import { ImportDialogComponent } from '@app/shared/components/import-dialog/import-dialog.component';
import { PermissionsService } from '@app/core/permissions/permissions.service';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root'
})
export abstract class RootService {
  [x: string]: any;
  //check if data loaded or not
  loaded: Subject<any> = new Subject();

  //send data from service to any component
  listData: Subject<any> = new Subject();
  // List Object which store the data from the server once it retrieve
  public resourcesList: any;
  customFilterActiveLink: string;
  resources: Subject<any> = new Subject();
  updateResources: Subject<any> = new Subject();
  appendResources$: Subject<any> = new Subject();
  storeResourceListResponse$: Subject<any> = new Subject();
  updateOnCreate = true;
  appendOnCreate = false;
  page_heading = false;
  customData: any = {};
  formInputsCategorized: any = {};
  itemID: number;
  links: MenuList[];
  lists: any = {
    // positions: { data: [] },
    // roles: { data: [] },
    // 'roles-create': { data: [] }
    // replaced with key : [] that holds data
  };

  lists$: Subject<any> = new Subject();
  show$: Subject<any> = new Subject();

  listsAdditions$: Subject<{ listPrefix: string; items: any[] }> = new Subject();

  dummyDataLoader = false;

  customCid: string;
  customTitle: string;

  public selectedItems: number[] = []; // for selecting multiple items for delete actions
  public selectedItems$: Subject<any> = new Subject();

  importData: any;
  clearInput: Subject<any> = new Subject();

  tabGroupActiveTab: {
    [key: string]: number;
  } = {};

  updateInputValue: Subject<{
    fieldProps?: ItemProps;
    fieldName?: string;
    fieldValue?: any;
    updateForm?: boolean;
  }> = new Subject();

  selectedAdditionalData: {
    key: string;
    unit: string;
    color?: string;
  }[];

  permissionsKey: string;
  permission: SystemPermission | boolean;
  showExportLoading: boolean;
  protected constructor(
    public router: Router,
    protected api: ApiRequestService,
    public permissionsService: PermissionsService,
    public shared: SharedService
  ) {
    // Initialize the resourceList as empty array.
    this.resourcesList = {};
    this.customEnvironment();
  }

  /**
   * Main method to get resource lists
   * @param params: params
   * @param archivedList: Flag to check if list is archived list
   * @param infinite: if you want to update list and add new items not replace old items
   * @param updateLists if you want to update existing list with new items and not load a new list over the existing
   */
  startAutoLoad(params: {} = {}, archivedList: boolean = false, infinite?: boolean, updateLists?: boolean) {
    return this.autoLoadResources(params, archivedList, infinite, updateLists);
  }

  /**
   * Get list of data
   * page
   * params
   */
  public autoLoadResources(params: {}, archivedList: boolean, infinite?: boolean, updateLists?: boolean) {
    if (this.routerPrefix() === '') {
      return;
    }
    return this.doGetLists(this.getFunctionURL(null, archivedList ? '/archived' : ''), params, infinite, updateLists);
  }

  async doGetLists(url: string, params: {} = {}, infinite?: boolean, updateLists?: boolean) {
    return this.api
      .get(url, params)
      .pipe(
        map(response => {
          return this.listPrepareOperations(response);
        })
      )
      .subscribe(async (resp: ApiResponse) => {
        if (updateLists) {
          this.storeResourceListResponseNewItems(resp, params, infinite);
          this.storeResourceListResponse$.next(null);
        } else {
          this.storeResourceListResponse(resp, params, infinite);
          this.storeResourceListResponse$.next(null);
        }
      });
  }

  /**
   * set response list to resourceList variable
   */
  storeResourceListResponse(resp: any, params?: any, infinite?: boolean) {
    if (infinite) {
      const newData = [...resp.data];
      const oldData = [...this.resourcesList.data];
      // this.resourcesList.data = [...oldData, ...newData];
      let appendedData: any[] = [];

      newData.forEach(newItem => {
        if (
          oldData.some(oldItem => {
            return oldItem.id === newItem.id;
          })
        ) {
          // return;
        } else {
          appendedData = [...appendedData, ...[newItem]];
        }
      });
      if (appendedData.length) {
        this.resourcesList.data = [...oldData, ...appendedData];
      }
      this.resourcesList.meta = resp.meta;
    } else {
      this.resourcesList = resp;
    }
    this.resources.next(null);
  }

  /**
   * set response list to resourceList variable
   */
  storeResourceListResponseNewItems(resp: any, params?: any, infinite?: boolean) {
    if (infinite) {
      const newData = [...resp.data];
      const oldData = [...this.resourcesList.data];
      const appendedData: any[] = [];
      newData.forEach(newItem => {
        if (
          oldData.some(oldItem => {
            return oldItem.id === newItem.id;
          })
        ) {
          // return;
        } else {
          appendedData.push(newItem);
        }
      });
      if (appendedData.length) {
        this.resourcesList.data = [...appendedData, ...oldData];
      }
      this.resourcesList.meta = resp.meta;
    } else {
      this.resourcesList = resp;
    }
    this.resources.next(null);
  }

  /**
   * Helper method to refactor the code before it's returned
   * @param data: Response
   */
  listPrepareOperations(data: {}) {
    return data;
  }

  /**
   * Helper Method, It'll overwrite from child services
   */
  routerPrefix(val: string = ''): string {
    return val ? val : '';
  }

  /**
   * Helper Method for different api route than frontend router prefix and component cid,
   * It'll overwrite from child services
   */
  apiRouterPrefix(val: string = null): string {
    return val;
  }

  /**
   * @returns the controller id that predefined in child service
   */
  get cid() {
    return this.routerPrefix();
  }

  getFunctionURL(action: any, suffix: string = '', prefix: string = '') {
    return action
      ? prefix + (this.apiRouterPrefix() ? this.apiRouterPrefix() : this.routerPrefix()) + '/' + action + suffix
      : prefix + (this.apiRouterPrefix() ? this.apiRouterPrefix() : this.routerPrefix()) + suffix;
  }

  /**
   * Archive Item
   */
  archive(id: number) {
    return this.doArchive(this.getFunctionURL('archive'), id);
  }

  /**
   * Archive Item Request
   * url
   * id
   */
  doArchive(url: string, id: number) {
    return this.api.put(url, { ids: [id] }).subscribe({
      next: (res: ApiResponse) => {
        this.afterSuccessListAction();
        this.shared.toastr.success(
          this.shared.translate.instant(this.cid + '.archived'),
          this.shared.translate.instant('common.success')
        );
        this.afterSuccessListAction();
        this.updateResources.next(null);
      },
      error: err => {
        this.errorHandle(err);
      }
    });
  }

  /**
   * Archive Items
   */
  archiveMultiple(ids: {}) {
    return this.doArchiveMultiple(this.getFunctionURL('archive'), { ids });
  }

  /**
   * Archive Items Request
   * url
   * id
   */
  doArchiveMultiple(url: string, body: any) {
    return this.api.put(url, body).subscribe({
      next: (res: ApiResponse) => {
        this.afterSuccessListAction();
        this.shared.toastr.success(
          this.shared.translate.instant(this.cid + '.archived'),
          this.shared.translate.instant('common.success')
        );

        this.updateResources.next(null);
      },
      error: err => {
        this.errorHandle(err);
      }
    });
  }

  /**
   * Archive Items
   */
  deleteMultiple(ids: {}) {
    return this.doDeleteMultiple(this.getFunctionURL(''), { ids });
  }

  /**
   * Archive Items Request
   * url
   * id
   */
  doDeleteMultiple(url: string, body: any) {
    return this.api.request('delete', url, body).subscribe({
      next: (res: ApiResponse) => {
        this.afterSuccessListAction();
        this.shared.toastr.success(res.message, this.shared.translate.instant('common.success'));
        this.updateResources.next(null);
      },
      error: err => {
        this.shared.toastr.error(err.error.message, this.shared.translate.instant('common.error'));
        console.log(err.error.message);
        this.errorHandle(err);
      }
    });
  }

  /**
   * Generic Restore
   * id: Item Id
   */
  restoreMultiple(ids: any) {
    return this.doRestoreMultiple(this.getFunctionURL(''), { ids });
  }

  /**
   * Restore Item Request
   * url
   * id
   */
  doRestoreMultiple(url: string, body: any) {
    return this.api.put(url + '/restore', body).subscribe({
      next: (res: ApiResponse) => {
        this.afterSuccessListAction();
        this.shared.toastr.success(
          this.shared.translate.instant(this.cid + '.restored'),
          this.shared.translate.instant('common.success')
        );

        this.updateResources.next(null);
      },
      error: err => {
        this.shared.toastr.error(err.error.message, this.shared.translate.instant('common.error'));
        this.errorHandle(err);
      }
    });
  }

  /**
   * Delete Item
   */
  delete(id: number) {
    return this.doDelete(this.getFunctionURL(id));
  }

  /**
   * Archive Item Request
   * url
   * id
   */
  doDelete(url: string) {
    return this.api.delete(url).subscribe({
      next: (res: ApiResponse) => {
        this.afterSuccessListAction();
        this.shared.toastr.success(res.message, this.shared.translate.instant('common.success'));
        this.updateResources.next(null);
      },
      error: err => {
        this.shared.toastr.error(err.error.message, this.shared.translate.instant('common.error'));
        this.errorHandle(err);
      }
    });
  }

  /**
   * selects items by ids as numbers or can use array of items to select them based on passed type: ex [5,2,3] or 5
   * @param id: ID
   */
  selectItem(id: any) {
    if (Array.isArray(id)) {
      if (this.selectedItems.length) {
        if (this.selectedItems.length === id.length) {
          id.forEach((y: number) => {
            if (this.selectedItems.find(x => x === y)) {
              const i = this.selectedItems.findIndex(x => x === y);
              this.selectedItems.splice(i, 1);
            } else {
              this.selectedItems.push(y);
            }
          });
        } else {
          id.forEach((y: number) => {
            if (this.selectedItems.find(x => x === y)) {
              // const i = this.selectedItems.findIndex(x => x === y);
              // this.selectedItems.splice(i, 1);
            } else {
              this.selectedItems.push(y);
            }
          });
        }
      } else {
        id.forEach((y: number) => {
          this.selectedItems.push(y);
        });
      }
      this.selectedItems$.next(null);
    } else if (typeof id === 'number') {
      if (this.selectedItems.length) {
        if (this.selectedItems.find(x => x === id)) {
          const i = this.selectedItems.findIndex(x => x === id);
          this.selectedItems.splice(i, 1);
        } else {
          this.selectedItems.push(id);
        }
      } else {
        this.selectedItems.push(id);
      }
      this.selectedItems$.next(null);
    }
  }

  clearSelectedList() {
    this.selectedItems = [];
    this.selectedItems$.next(null);
  }

  /**
   * Generic Update Status
   * id: Item Id
   * data: New Item Data
   */
  toggleActivate(id: any) {
    return this.doToggleActivateItem(this.getFunctionURL(`${id}/status`), {});
  }

  /**
   * Generic Restore
   * id: Item Id
   */
  restore(id: number) {
    return this.doRestore(this.getFunctionURL('restore'), id);
  }

  /**
   * Restore Item Request
   * url
   * id
   */
  doRestore(url: string, data: any) {
    return this.api.put(url, { ids: [data] }).subscribe({
      next: (res: ApiResponse) => {
        this.shared.toastr.success(
          this.shared.translate.instant(this.cid + '.restored'),
          this.shared.translate.instant('common.success')
        );

        this.afterSuccessListAction();
        this.updateResources.next(null);
      },
      error: err => {
        this.shared.toastr.error(err.error.message, this.shared.translate.instant('common.error'));
        this.errorHandle(err);
      }
    });
  }

  /**
   * Generic Update Status
   */
  async doToggleActivateItem(url: string, data: {}) {
    return this.api.put(url, data).subscribe({
      next: async (resp: ApiResponse) => {
        this.shared.toastr.success(
          this.shared.translate.instant(this.cid + '.status_changed'),
          this.shared.translate.instant('common.success')
        );

        this.afterSuccessListAction();
        return this.updateResources.next(null);
        // });
      },
      error: async err => {
        this.shared.toastr.error(err.error.message, this.shared.translate.instant('common.error'));
        this.errorHandle(err);
      }
    });
  }

  checkSelected(id: number) {
    return !!this.selectedItems.find(x => x === id);
  }

  /**
   * Generic Show Action Request
   * @param id: Item Identifier
   */
  showItem(id: number) {
    return this.doShow(this.getFunctionURL(`${id}`));
  }

  doShow(url: string) {
    return this.api.get(url).pipe(
      map((response: ApiResponse) => {
        return this.refactorItem(response.data);
      })
    );
  }

  /**
   * Generic Create Action Request
   * @param data: New Item Data
   */
  createItem(data: {}) {
    return this.doCreate(this.getFunctionURL(''), data);
  }

  /**
   * Create Item Request
   * url
   * id
   */
  doCreate(url: string, data: {}) {
    return this.api.post(url, data);
  }

  /**
   * Generic Create Action Request
   * id: Item Id
   * data: New Item Data
   */
  updateItem(id: any, data: {}) {
    return this.doUpdateItem(this.getFunctionURL(id), data);
  }

  /**
   * Generic Update Action Request
   */
  doUpdateItem(url: string, data: {}) {
    return this.api.put(url, data);
  }

  /**
   * Generic update Action Request
   * id: Item Id
   * data: New Item Data
   */
  updateItemAsPost(id: any, data: {}) {
    return this.doUpdateItemAsPost(this.getFunctionURL(id), data);
  }

  /**
   * Generic Update Action Request
   */
  doUpdateItemAsPost(url: string, data: {}) {
    return this.api.post(url, data);
  }

  errorHandle(err: {}) {
    for (const error of Object.keys(err)) {
      // this.presentToast(err[error], 'danger');
    }
  }

  navigateToList() {
    return this.shared.dialog.closeAll();
  }

  refactorItem(item: {}) {
    this.loaded.next(true);
    return item;
  }
  /**
   * Load lists Request
   */
  getLists(field: string, page: number = 1) {
    return this.api
      .get(field)
      .pipe(
        map(response => {
          return this.refactorListsData(field, response);
        })
      )
      .subscribe({
        next: (resp: ApiResponse) => {
          this.storeListsResponse(field, resp, page);
        },
        error: err => this.errorHandle(err)
      });
  }

  refactorListsData(field: string, response: {}) {
    return response;
  }

  /**
   * set field response list to lists variable
   */
  storeListsResponse(field: string, resp: ApiResponse, page: number) {
    if (page === 1) {
      // this.lists[field].data.length = 0;
      const fieldName = field.replace('/', '-');
      this.lists[fieldName] = resp.data;

      // this.lists[field].data = resp.data;
    } else {
      // this.lists[field].data = this.lists[field].data.concat(resp.response.data);
    }
  }

  /**
   * define the grid list items (columns)
   */
  abstract get featureProps(): ItemProps[];

  /**
   * // todo : update this function docs
   * @param url
   */

  navigateUrl(url: any) {
    return this.router.navigate([url]);
  }

  afterSuccessListAction() {
    this.clearSelectedList();
  }

  refactorFormBeforeSubmit(formValue: any) {
    return formValue;
  }

  /**
   * Pass the value to deep find method using path and original object
   * obj
   * path
   */
  getValue(obj: any, path: string) {
    return AppHelper.deepFind(obj, path);
  }

  /**
   * custom actions used in lists
   * @param action to be done ex open dashboard of vendors
   * @param row > table row
   * @param column > table column
   * @param staticValue to do action on it instead of the row and column and prop method of doing action on a column value
   * @param fnRow
   * @param fn
   */
  listActions(
    action: string,
    row: any,
    column: any,
    fnRow?: (row: any, column: any) => void,
    fn?: (column: any) => void
  ) {
    if (!action) {
      if (fnRow) {
        return fnRow(row, column);
      } else if (fn) {
        return fn(column);
      } else {
        return;
      }
    }

    let value: any = '';
    if (column.list.action.prop) {
      value = this.getValue(row, column.list.action.prop);
    } else {
      value = this.getValue(row, column.prop);
    }

    switch (action) {
      case 'mailto': {
        window.open(action + ':' + value);
        // return this.router.navigateByUrl(action + ':' + value);
        break;
      }
      case 'copyField': {
        this.copyToClipboard(value);
        break;
      }
      case 'openItemDashboard': {
        this.navigateUrl(`${this.cid}/dashboard/${value}`);
        break;
      }
      case 'openItemDashboardNewTab': {
        // this.navigateUrl(`${this.cid}/dashboard/${value}`);
        window.open(`${this.cid}/dashboard/${value}`, '_blank');
        break;
      }
      case 'goToUrl': {
        window.open(value, '_blank');
        break;
      }
      case 'showPermissionsDetails': {
        this.show$.next(row);
        break;
      }

      case 'showContracts': {
        this.show$.next(row);
        break;
      }
      case null: {
        break;
      }
    }
  }

  copyToClipboard(value: any) {
    const text = JSON.stringify(value);
    AppHelper.copyToClipboard(text);
    this.shared.toastr.info(
      `${text} ` + this.shared.translate.instant('common.copied_to_clipboard'),
      this.shared.translate.instant('common.success')
    );
  }

  /**
   * Action dialog for actions that need dialog confirmation before doing the action
   * @param service of the module (usually root service until overridden by specific module that extends from root service)
   * @param action string for the name of the function in a switch
   * case inside dialogAction function in root service which could have
   * generic functions and or specific implementations inside each service that extend from root service
   * @param title dialog title translation string
   * @param message dialog message translation string
   * @param submitText dialog submit text on button
   * @param cancelText dialog cancel text on button
   * @param data  data to be passed along to the dialog and from dialog to function executed from service
   * @param submitCssClass css class of the submit button
   */
  openActionDialog(
    service: any,
    action: string,
    title: string,
    message: string,
    submitText: string,
    cancelText: string,
    data?: any,
    submitCssClass?: any
  ): void {
    this.shared.dialog.open(DynamicDialogComponent, {
      data: {
        service,
        action,
        title,
        message,
        submitText,
        cancelText,
        data,
        submitCssClass
      }
    });
  }

  /**
   * function for all actions that's passed to and from the dialog
   * @param action string for action name passed before open dialog
   * @param data object to be used in each function passed from the function that opened the dialog
   */
  dialogAction(action: string, data?: any) {
    switch (action) {
      case 'cancel': {
        this.goBack();
        break;
      }
      case 'restoreMultiple': {
        this.restoreMultiple(data);
        break;
      }
      case 'restore': {
        this.restore(data);
        break;
      }
      case 'deleteMultiple': {
        this.deleteMultiple(data);
        break;
      }
      case 'delete': {
        this.delete(data);
        break;
      }
      case 'archiveMultiple': {
        this.archiveMultiple(data);
        break;
      }
      case 'archive': {
        this.archive(data);
        break;
      }
    }
  }

  /**
   * Extend from openActionDialog() method
   * @param data: id or [ids] of selected items
   * @param action: action type [restore, archive, delete]
   */
  openDialog(data: any, action: string) {
    this.openActionDialog(
      this,
      action,
      `common.confirm_${action}.title`,
      `common.confirm_${action}.message`,
      `common.confirm_${action}.submitText`,
      `common.confirm_${action}.cancelText`,
      data,
      action === 'restore' ? 'bg-success' : 'bg-danger'
    );
  }

  /**
   * Export Items
   */
  exportMultiple(ids?: number[], all: boolean = false, value?: any, params?: any) {
    let exportBody: any;
    let exportUrl = { xlsx: 'excel', pdf: 'pdf' };
    exportBody = {
      fileType: 'xlsx'
    };
    if (value) {
      exportBody.fileType = value;
    }
    if (ids && ids.length > 0 && all === false) {
      exportBody.ids = ids;
    }
    //  console.log('params export', params);
    //  if (params && params.page) {
    //   delete params.page;
    // }
    return this.doExportMultiple(
      this.getFunctionURL('export/' + (exportUrl as any)[exportBody.fileType]),
      exportBody,
      params
    );
  }

  /**
   * Export Items Request
   * url
   * id
   */
  doExportMultiple(url: string, body: any, params?: any) {
    const today = new Date();
    const date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
    const time = today.getHours() + '-' + today.getMinutes() + '-' + today.getSeconds();
    const dateTime = date + '_' + time;

    const fileName = `${this.cid}_${dateTime}.${body.fileType}`;
    if (body.fileType === 'pdf') url = url + '?output=raw';
    this.showExportLoading = true;
    return this.api.request('GET', url, body, {}, params, 'blob').subscribe({
      next: res => {
        saveAs(res, fileName);
        this.showExportLoading = false;
        this.afterSuccessListAction();
        this.shared.toastr.success(
          this.shared.translate.instant(this.cid + '.exported'),
          this.shared.translate.instant('common.success')
        );
      },
      error: err => {
        this.showExportLoading = false;
        this.errorHandle(err);
      }
    });
  }

  /**
   * Get data form server
   * @param componentName: Component Name
   * @param itemID: Resource id
   * @param isCreate: flag
   * @param headers: Request headers
   * @param filterMethod: is this method filtering the results
   */
  getComponentData(
    componentName: string,
    itemID: number,
    headers: {} = {},
    isCreate: boolean = false,
    filterMethod: boolean = false
  ) {
    this.api
      .get(this.getFunctionURL(`${itemID}/${componentName}`), headers)
      .pipe(
        map(data => {
          return data;
        })
      )
      .subscribe(obj => {
        return this.storeComponentData(componentName, obj, isCreate, filterMethod);
      });
  }

  /**
   * post data to server
   * @param componentName: Component Name
   * @param itemID: Resource id
   * @param data: form data
   * @param isCreate: Check if post type is Create or update
   */
  postComponentData(componentName: string, itemID: number, data: any, isCreate: boolean = false) {
    this.api
      .post(this.getFunctionURL(`${itemID}/${componentName}`), data)
      .pipe(
        map((response: any) => {
          return response;
        })
      )
      .subscribe({
        next: obj => {
          this.shared.toastr.success(obj.message);
          return this.getComponentData(componentName, itemID, null, isCreate);
        },
        error: error => {
          if (error && error.status === 422) {
            const errorObj = { componentName: componentName, error: error.error };
            this.shared.dashboardErrors$.next(errorObj);
          }
        }
      });
  }

  /**
   * Delete Item from resource nested resources
   * @param componentName: Component Name
   * @param id: deleting item id
   * @param itemID: Resource id
   */
  deleteComponentData(componentName: string, id: number, itemID: number) {
    this.api
      .delete(this.getFunctionURL(`${componentName}/${id}`))
      .pipe(
        map((data: any) => {
          return data;
        })
      )
      .subscribe(obj => {
        this.shared.toastr.success(obj.message);
        this.getComponentData(componentName, itemID);
        return this.shared.handleItemById({ componentName, id });
      });
  }

  /**
   * store response data in subject subscription
   * It override in each service
   * @param componentName: Component Name
   * @param data: response data
   * @param isCreate: isCreate
   * @param filterMethod: is this method filtering the data?
   */
  storeComponentData(componentName: string, data: any, isCreate: boolean = false, filterMethod: boolean = false) {
    return { component: componentName, data, isCreate, filterMethod };
  }

  apiRequest(
    method: string,
    url: string,
    body: {} = {},
    headers?: any,
    params?: any,
    responseType?: any
  ): Observable<any> {
    return this.api.request(method, url, body, headers, params, responseType);
  }

  /**
   * generic method to be overridden in specific services to refactor filters in each module service as needed
   * @param filters passed filters object
   */
  refactorFilters(filters: any) {
    this.featureProps.forEach(featureProp => {
      if (featureProp.list && featureProp.list.filterFieldType === 'datepicker') {
        const dateValue = filters[featureProp.name];
        if (dateValue) {
          const convertedDate = this.shared.momentFormat(dateValue);
          filters[featureProp.name] = convertedDate;
        }
      }
    });
    return filters;
  }

  /**
   * generic method to be overridden in specific services to refactor filters before converting
   * them into query parameters in each module service as needed
   * @param filters passed filters object
   */
  refactorFiltersBeforeQueryParams(filters: any) {
    return filters;
  }

  /**
   * generic method to be overridden in specific services to refactor sorting in each module service as needed
   * @param sorting passed sorting object
   */
  refactorSorting(sorting: any) {
    return sorting;
  }

  customEnvironment() {
    if (customEnvironment.dummyDataLoader) {
      this.dummyDataLoader = true;
    }
  }

  loadDummyData(cid: string = this.cid) {
    this.storeResourceListResponse(this.listPrepareOperations((DummyData as any)[cid]));
  }

  returnDummyData(dummyVar: string) {
    return (DummyData as any)[dummyVar];
  }

  /**
   * compare fn to compare current date to another date by a compare keyword to be overridden as needed in each separate module service
   * @param inputDate to compare
   * @param compareKeyword compare string of switch case
   */
  dateAgoChecker(inputDate: any, compareKeyword?: string): boolean {
    const now = moment(new Date());
    let compareTo = now;
    if (compareKeyword === 'yesterday') {
      compareTo = now.subtract(1, 'days');
    }
    const date = moment(inputDate);
    return date > compareTo;
  }

  /**
   * helper fn to convert any type of data into date with moment library
   * @param date string
   */
  dateConverter(date: any) {
    return moment(date);
    // return moment(new Date());
  }

  openImportDialog(service: RootService) {
    this.shared.dialog.open(ImportDialogComponent, {
      data: {
        service
      },
      panelClass: 'import-dialog'
    });
  }

  onChangeValue_ng_select(column: ItemProps, event: any) {
    if ((column.list && column.list.onChange) || (column.form && column.form.onChange)) {
      this.onChangedValue(column, event);
    } else {
      return;
    }
  }

  onChangedValue(column: ItemProps, event: any) {}

  loadSelectList(fieldName: string, url?: string, type?: string) {
    const field = this.featureProps.find(x => x.name === fieldName);
    if (url && type) {
      this.resourceGet(url, (this.getFieldByName(fieldName) as any)[type].listPaginate).subscribe((result: any) => {
        this.lists[(field as any)[type].listPrefix] = this.refactorSelectListsResponse(
          (field as any)[type].listPrefix,
          result,
          (field as any)[type].listPaginate
        );
        this.lists$.next((field as any)[type].listPrefix);
      });
    } else {
      if (field.list.dataUrl) {
        this.resourceGet(field.list.dataUrl, (this.getFieldByName(fieldName) as any)[type].listPaginate).subscribe(
          (result: any) => {
            this.lists[field.list.listPrefix] = this.refactorSelectListsResponse(
              field.list.listPrefix,
              result,
              field.list.listPaginate
            );
            this.lists$.next(field.list.listPrefix);
          }
        );
      } else if (field.form.dataUrl) {
        this.resourceGet(field.form.dataUrl, (this.getFieldByName(fieldName) as any)[type].listPaginate).subscribe(
          (result: any) => {
            this.lists[field.form.listPrefix] = this.refactorSelectListsResponse(
              field.form.listPrefix,
              result,
              field.form.listPaginate
            );
            this.lists$.next(field.form.listPrefix);
          }
        );
      } else {
        return;
      }
    }
  }

  clearSelectList(fieldName?: string, listPrefix?: string) {
    let _listprefix: string;
    if (fieldName) {
      const field: ItemProps = this.getFieldByName(fieldName);
      if (field.list && field.list.listPrefix) {
        _listprefix = field.list.listPrefix;
      } else if (field.form && field.form.listPrefix) {
        _listprefix = field.form.listPrefix;
      }
    } else if (listPrefix) {
      _listprefix = listPrefix;
    }
    if (this.lists[_listprefix]) {
      this.lists[_listprefix] = [];
    }
  }

  getFieldByName(fieldName: string) {
    if (fieldName) {
      return this.featureProps.find(x => x.name === fieldName);
    }
  }

  loadSelectLists(type?: string) {
    this.featureProps.forEach(field => {
      if (
        (field.list &&
          field.list.filterFieldType &&
          field.list.dataUrl &&
          field.list.listPrefix &&
          (field.list.searchable || field.list.searchableInline)) ||
        (field.form && field.form.formFieldType && field.form.dataUrl && field.form.listPrefix)
      ) {
        if (type && type === 'list') {
          if (field && field.list && field.list.dataUrl && field.list.listPrefix) {
            this.resourceGet(field.list.dataUrl, field.list.listPaginate).subscribe((result: any) => {
              this.lists[field.list.listPrefix] = this.refactorSelectListsResponse(
                field.list.listPrefix,
                result,
                field.list.listPaginate
              );
              this.lists$.next(field.list.listPrefix);
            });
          }
        } else if (type && type === 'form') {
          if (field && field.form && field.form.dataUrl && field.form.listPrefix) {
            this.resourceGet(field.form.dataUrl, field.form.listPaginate).subscribe((result: any) => {
              this.lists[field.form.listPrefix] = this.refactorSelectListsResponse(
                field.form.listPrefix,
                result,
                field.form.listPaginate
              );
              this.lists$.next(field.form.listPrefix);
              this.listData.next({
                data: this.lists[field.form.listPrefix],
                listPrefix: field.form.listPrefix,
                dataUrl: field.form.dataUrl
              });
            });
          }
        } else {
          if (field && field.list && field.list.dataUrl && field.list.listPrefix) {
            this.resourceGet(field.list.dataUrl, field.list.listPaginate).subscribe((result: any) => {
              this.lists[field.list.listPrefix] = this.refactorSelectListsResponse(
                field.form.listPrefix,
                result,
                field.form.listPaginate
              );
              this.lists$.next(field.list.listPrefix);
            });
          } else if (field && field.form && field.form.dataUrl && field.form.listPrefix) {
            this.resourceGet(field.form.dataUrl, field.form.listPaginate).subscribe((result: any) => {
              this.lists[field.form.listPrefix] = this.refactorSelectListsResponse(
                field.form.listPrefix,
                result,
                field.form.listPaginate
              );
              this.lists$.next(field.form.listPrefix);
            });
          }
        }
      }
    });
  }

  refactorSelectListsResponse(listPrefix: string, response: any, listPaginate: boolean = false) {
    if (response.data && Array.isArray(response.data)) {
      if (response.data) {
        if (listPaginate) {
          return response;
        } else {
          return response.data;
        }
      }
    } else {
      if (response.data) {
        var arr = [];
        for (const key in response.data) {
          response.data[key]['id'] = key;
          arr.push(response.data[key]);
        }
        response.data = arr;
        return response.data;
      }
    }
  }

  /**
   * returns observable from component url into a data observable for async loading resources in template
   * @param apiPath path of api resource request
   * @param pagination request paginated or not
   * @param params parameters to the request
   */
  resourceGet(apiPath: string, pagination: boolean = false, params: any = {}) {
    if (!pagination) {
      //params.page = 0;
      params.all_pages = 1;
    }
    return this.apiRequest('GET', apiPath, {}, {}, params);
  }

  downloadSampleFile(url: string, fileType: string) {
    const today = new Date();
    const date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
    const time = today.getHours() + '-' + today.getMinutes() + '-' + today.getSeconds();
    const dateTime = date + '_' + time;

    const fileName = `${this.cid}_${dateTime}.${fileType}`;

    return this.api.request('GET', url, null, {}, {}, 'blob').subscribe({
      next: res => {
        saveAs(res, fileName);
        this.afterSuccessListAction();
        this.shared.toastr.success(
          this.shared.translate.instant(this.cid + '.sampleFileGenerated'),
          this.shared.translate.instant('common.success')
        );
      },
      error: err => {
        this.errorHandle(err);
      }
    });
  }

  toFormData<T>(formValue: T, _method?: string) {
    const formData = new FormData();

    for (const key of Object.keys(formValue)) {
      const value = (formValue as any)[key];
      /**
       * fix for boolean values in formData method
       */
      if (value === true) {
        formData.append(key, '1');
      } else if (value === false) {
        formData.append(key, '0');
      } else {
        formData.append(key, value);
      }
    }
    if (_method) {
      formData.append('_method', _method);
    }
    return formData;
  }

  navigateUrlOverride(url: string, type?: string, id?: number) {
    return url;
  }

  closeIssue(id: number) {}

  reopenIssue(id: number) {}

  goBack() {
    // this.shared.location.back();
    this.shared.dialog.closeAll();
  }

  setActiveTab(tabGroup: string, tabName: number) {
    this.tabGroupActiveTab[tabGroup] = tabName;
  }

  clearActiveTabs() {
    Object.keys(this.tabGroupActiveTab).forEach(key => {
      this.tabGroupActiveTab[key] = 0;
    });
  }

  getActiveTab(tabGroup: string): number {
    return this.tabGroupActiveTab[tabGroup] ? this.tabGroupActiveTab[tabGroup] : 0;
  }

  /**
   * helper method to patch some values in form
   * to be overridden on specific services
   */
  patchDefaultValues() {
    // const userName = this.credentialsService.credentials.userId;
    // const currentDate = this.shared.moment().format('YYYY-MM-DD');
    // this.updateInputValue.next({fieldName: 'name', fieldValue: userName, updateForm: true})
    // this.updateInputValue.next({fieldName: 'contractStart', fieldValue: currentDate, updateForm: true})
  }

  actionOnCreatedItem(id: number) {}

  /**
   * @param formValue : form value
   * @param controls : control names to be removed from form value
   */
  removeFormControlValue(formValue: any, controls: string[]) {
    if (formValue && controls && controls.length) {
      controls.forEach(control => {
        if (formValue.hasOwnProperty(control)) {
          delete formValue[control];
        }
      });
    }
  }

  setModulePermissions() {
    this.permission = this.permissionsService.getPermissionsByModuleHelper(this.permissionsKey);
  }

  // setPermissionsModuleKey() {
  //   // this.permissionsKey = this.shared.getPermissionsModuleKey();
  // }

  // getFormControl(name: string, path: string = null) {
  //   // get the name string value from the mapper
  //   const fieldName = path || this.fieldMapper(name);
  //   // if not found return with form getter to throw form error
  //   // the name should be changed to a valid one then
  //   // if found but its a single value, return it
  //   if (!fieldName || fieldName.split('.').length == 1) return this.form.get(name);
  //   const map = fieldName.split('.');
  //   // map = [arrayname, index, control]
  //   const formGroup = this.form.get(map[0]);
  //   const formControl = formGroup['controls'][map[1]].get(map[2]);
  //   return formControl;
  // }
  fieldMapper(name: string): string {
    return name;
  }
  /**
   * helper method to get Permissions from Permissions Service that could be used in any service
   * extending root service as it imports this shared service
   * @param module: module permission key
   * @param key: sub module permissions key
   */
  // public getPermissions(module?: string, key?: string): boolean | SystemPermissions {
  //   return this.permissionsService.getPermissions(module, key);
  // }

  /**
   * to filter the list
   * @param filterBy key value pair
   */
  filter(filterBy: FilterObject) {
    this.customFilterActiveLink = filterBy.value;
    this.shared.utilitiesService.setFilters(this.refactorFilters(filterBy));
  }

  onScrollToEnd(field: any, event: any) {}
}
